/**
 * BulkLoadStock.jsx — iter169
 *
 * Admin-only warehouse-setup tool: upload a CSV with sku_code, location_code,
 * quantity (+ optional batch_number, status) and bulk-create batches +
 * inventory rows. Dry-run first to validate, then commit.
 */
import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  ArrowDown, FileArrowUp, Warning, CheckCircle, Eye,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { bulkLoadStock, downloadBulkLoadTemplate } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

export default function BulkLoadStock() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('append'); // 'append' | 'replace'
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Hard-gate non-admin users out of this page (defence in depth — the
  // backend also returns 403, but a visible redirect is clearer than a toast).
  if (user && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleUpload = async (asDryRun) => {
    if (!file) {
      toast.error('Pick a CSV file first');
      return;
    }
    setBusy(true);
    try {
      const r = await bulkLoadStock(file, mode, asDryRun);
      setResult(r.data);
      if (asDryRun) {
        toast.success(`Preview ready: ${r.data.valid_rows} valid · ${r.data.error_rows} error(s)`);
      } else {
        toast.success(`Committed: ${r.data.inventory_rows_created} inventory row(s) created`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl space-y-6" data-testid="bulk-load-stock-page">
        <div>
          <p className="overline">Inventory · Setup</p>
          <h1 className="text-2xl font-bold">Bulk-load stock from CSV</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Admin-only setup tool. Upload a CSV with{' '}
            <span className="font-mono">sku_code, location_code, quantity</span> (and optional{' '}
            <span className="font-mono">batch_number, status</span>) to seed your warehouse in seconds.
          </p>
        </div>

        {/* --- Step 1: template --------------------------------------------- */}
        <section className="border border-border bg-card p-5 space-y-2" data-testid="step-template">
          <div className="overline">Step 1 — Download the template</div>
          <Button variant="outline" onClick={downloadBulkLoadTemplate} data-testid="download-template-btn">
            <ArrowDown size={14} className="mr-1" /> Download CSV template
          </Button>
        </section>

        {/* --- Step 2: upload + options ------------------------------------- */}
        <section className="border border-border bg-card p-5 space-y-4" data-testid="step-upload">
          <div className="overline">Step 2 — Choose file &amp; options</div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              CSV file
            </label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="block mt-2 text-xs"
              data-testid="bulk-load-file-input"
            />
            {file && (
              <p className="mt-1 text-[11px] text-muted-foreground font-mono">
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Load mode
            </label>
            <div className="mt-2 flex gap-3 text-xs">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="mode-append-label">
                <input
                  type="radio" name="mode" value="append"
                  checked={mode === 'append'}
                  onChange={() => setMode('append')}
                  data-testid="mode-append"
                />
                <span><strong>Append</strong> — add to any existing stock (safe)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="mode-replace-label">
                <input
                  type="radio" name="mode" value="replace"
                  checked={mode === 'replace'}
                  onChange={() => setMode('replace')}
                  data-testid="mode-replace"
                />
                <span><strong>Replace</strong> — overwrite (destructive)</span>
              </label>
            </div>
            {mode === 'replace' && (
              <div className="mt-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 flex items-start gap-2">
                <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
                <span>Replace mode <strong>deletes</strong> all existing inventory rows for each
                  variant+location pair touched by the CSV. Use only during initial setup.</span>
              </div>
            )}
          </div>
        </section>

        {/* --- Step 3: validate + commit ----------------------------------- */}
        <section className="border border-border bg-card p-5 space-y-3" data-testid="step-actions">
          <div className="overline">Step 3 — Validate &amp; commit</div>
          <p className="text-[11px] text-muted-foreground">
            Always run <strong>Validate (dry-run)</strong> first to see what would happen — no data is written.
            When the row counts look right, click <strong>Commit</strong>.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline" disabled={busy || !file}
              onClick={() => { setDryRun(true); handleUpload(true); }}
              data-testid="validate-dry-run-btn"
            >
              <Eye size={14} className="mr-1" />
              {busy && dryRun ? 'Validating…' : 'Validate (dry-run)'}
            </Button>
            <Button
              disabled={busy || !file || !(result?.valid_rows > 0)}
              onClick={() => {
                if (!window.confirm(
                  `Commit ${result?.valid_rows} row(s) into inventory using "${mode}" mode? `
                  + (mode === 'replace' ? 'This will OVERWRITE existing stock at those locations.' : '')
                )) return;
                setDryRun(false); handleUpload(false);
              }}
              className="bg-brand hover:bg-brand/90"
              data-testid="commit-btn"
            >
              <FileArrowUp size={14} className="mr-1" />
              {busy && !dryRun ? 'Committing…' : 'Commit'}
            </Button>
          </div>
        </section>

        {/* --- Result panel ------------------------------------------------- */}
        {result && (
          <section
            className="border border-border bg-card p-5 space-y-3"
            data-testid="result-panel"
          >
            <div className="flex items-center gap-2">
              {result.committed ? (
                <CheckCircle size={20} weight="fill" className="text-emerald-600" />
              ) : (
                <Eye size={20} weight="fill" className="text-blue-600" />
              )}
              <h2 className="text-base font-bold">
                {result.committed ? 'Committed' : 'Dry-run preview'}
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <Stat label="Total rows" value={result.total_rows} />
              <Stat label="Valid" value={result.valid_rows} good />
              <Stat label="Errors" value={result.error_rows} warn={result.error_rows > 0} />
              {result.committed && (
                <Stat label="Inventory rows created" value={result.inventory_rows_created} good />
              )}
            </div>

            {result.errors.length > 0 && (
              <div className="mt-3">
                <div className="overline mb-2">Errors (first {result.errors.length}{result.errors_truncated ? ' of many' : ''})</div>
                <div className="border border-border max-h-96 overflow-y-auto" data-testid="error-list">
                  <table className="w-full text-[11px]">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-2 py-1 w-12">Row</th>
                        <th className="text-left px-2 py-1">SKU</th>
                        <th className="text-left px-2 py-1">Location</th>
                        <th className="text-left px-2 py-1">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((err, i) => (
                        <tr key={i} className="border-t border-border" data-testid={`error-row-${err.row}`}>
                          <td className="px-2 py-1 font-mono">{err.row}</td>
                          <td className="px-2 py-1 font-mono">{err.sku_code || '—'}</td>
                          <td className="px-2 py-1 font-mono">{err.location_code || '—'}</td>
                          <td className="px-2 py-1 text-red-700">{err.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.committed && result.replace_keys_purged > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {result.replace_keys_purged} existing variant+location pair(s) were purged before insert.
              </p>
            )}
          </section>
        )}
      </div>
    </Layout>
  );
}

const Stat = ({ label, value, good = false, warn = false }) => (
  <div className={`border ${warn ? 'border-amber-300 bg-amber-50/40' : good ? 'border-emerald-300 bg-emerald-50/40' : 'border-border'} px-3 py-2`}>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`mt-0.5 text-xl font-bold font-mono tabular-nums ${warn ? 'text-amber-700' : good ? 'text-emerald-700' : ''}`}>
      {value}
    </p>
  </div>
);
